import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { LoadingState } from "../../components/UI/States";
import { Notice } from "../../components/Fleet/FleetParts";
import { fetchSafetyOverview } from "./safetyApi";
import { ListCard, percent, plural } from "./SafetyParts";
import { SAFETY_PAGES } from "./safetyPages";

// Safety Overview (Admin / Safety Manager): one card per safety area with
// the number that needs attention, and a per-branch table whose numbers
// link into each area's own page, filtered to that branch.

const NUMBER_TONE = {
  rose: "text-rose-600 dark:text-rose-300",
  amber: "text-amber-600 dark:text-amber-300",
  emerald: "text-emerald-600 dark:text-emerald-300",
  default: "",
};

const AreaCard = ({ page, value, label, detail, tone = "default" }) => (
  <Link
    to={page.path}
    className="app-card group flex items-start gap-4 rounded-2xl border p-5 transition hover:-translate-y-0.5 hover:border-rose-300"
  >
    <span className={`app-icon-tile h-11 w-11 shrink-0 rounded-2xl ${page.tile}`}>
      <page.icon />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block font-semibold">{page.name}</span>
      <span className="mt-1 flex items-baseline gap-2">
        <span className={`text-3xl font-bold tabular-nums tracking-tight ${NUMBER_TONE[tone]}`}>{value}</span>
        <span className="app-muted text-sm">{label}</span>
      </span>
      {detail && <span className="app-muted mt-1 block text-sm">{detail}</span>}
    </span>
    <ChevronRightRoundedIcon className="app-muted mt-1 shrink-0 transition group-hover:translate-x-0.5" />
  </Link>
);

// A scorecard number that opens the area's page filtered to the branch.
const Cell = ({ to, value, tone }) => (
  <Link
    to={to}
    className={`inline-block min-w-[2rem] rounded-md px-1.5 py-0.5 text-right tabular-nums hover:bg-[color:var(--app-hover)] ${
      value ? `font-semibold ${NUMBER_TONE[tone]}` : "app-muted"
    }`}
  >
    {value}
  </Link>
);

const SafetyOverview = () => {
  const user = useSelector(selectUser);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    fetchSafetyOverview(user.id)
      .then(setOverview)
      .catch((e) => setError(e.message));
  }, [user?.id]);

  const c = overview?.concerns;
  const ext = overview?.extinguishers;
  const t = overview?.training;
  const latest = overview?.toolbox.latest;
  const openConcerns = c ? c.open + c.inReview : 0;
  const q = (branch) => `?branch=${encodeURIComponent(branch)}`;

  return (
    <PageShell size="xl">
      <PageHeader
        icon={HealthAndSafetyRoundedIcon}
        tone="rose"
        eyebrow="Safety"
        title="Safety Overview"
        description="What needs attention across every branch. Pick an area for the details."
      />

      {error && <Notice tone="rose">{error}</Notice>}
      {!overview && !error && <LoadingState label="Loading safety data…" />}

      {overview && (
        <>
          <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-2">
            <AreaCard
              page={SAFETY_PAGES.concerns}
              value={openConcerns}
              label={openConcerns === 1 ? "open concern" : "open concerns"}
              tone={openConcerns ? "rose" : "emerald"}
              detail={`${c.inReview} in review · ${c.reportedRecently} reported in the last ${overview.rules.recentDays} days`}
            />
            <AreaCard
              page={SAFETY_PAGES.extinguishers}
              value={ext.due}
              label="due for inspection"
              tone={ext.due || ext.failed ? "amber" : ext.total ? "emerald" : "default"}
              detail={
                ext.total
                  ? [
                      `${ext.neverInspected} never inspected`,
                      ext.failed ? `${ext.failed} failed last check` : null,
                      `${ext.total} total`,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "None added yet"
              }
            />
            <AreaCard
              page={SAFETY_PAGES.training}
              value={t.overdue}
              label="overdue"
              tone={t.overdue ? "rose" : t.assigned ? "emerald" : "default"}
              detail={[
                t.assigned ? `${percent(t.completed, t.assigned)}% of ${t.assigned} assignments done` : "Nothing assigned",
                t.dueSoon ? `${t.dueSoon} due this week` : null,
                t.unassignedVideos ? `${plural(t.unassignedVideos, "video")} not assigned yet` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            />
            <AreaCard
              page={SAFETY_PAGES.toolbox}
              value={latest ? latest.done : "—"}
              label={latest ? "finished the latest talk" : "no talks yet"}
              detail={
                latest &&
                `${latest.title}${
                  latest.quizEnabled && latest.quizTaken ? ` · ${latest.quizPassed} of ${latest.quizTaken} passed the quiz` : ""
                }`
              }
            />
          </div>

          <ListCard title="By branch" description="Click a number to see that branch's details.">
            <div className="mt-3 overflow-x-auto border-t app-divider">
              <table className="app-table">
                <thead>
                  <tr>
                    <th>Branch</th>
                    <th className="text-right">Open concerns</th>
                    <th className="text-right">Extinguishers due</th>
                    <th className="text-right">Training overdue</th>
                    <th className="text-right" title={latest ? `Finished “${latest.title}”` : undefined}>
                      Latest talk
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {overview.branches.map((row) => (
                    <tr key={row.branch}>
                      <td className="font-semibold">{row.branch}</td>
                      <td className="text-right">
                        <Cell to={`${SAFETY_PAGES.concerns.path}${q(row.branch)}`} value={row.concernsOpen} tone="rose" />
                      </td>
                      <td className="text-right">
                        {row.extinguishers ? (
                          <Cell
                            to={`${SAFETY_PAGES.extinguishers.path}${q(row.branch)}&status=attention`}
                            value={row.extinguishersDue}
                            tone="amber"
                          />
                        ) : (
                          <span className="app-muted text-xs">None added</span>
                        )}
                      </td>
                      <td className="text-right">
                        {row.trainingAssigned ? (
                          <Cell to={`${SAFETY_PAGES.training.path}${q(row.branch)}`} value={row.trainingOverdue} tone="rose" />
                        ) : (
                          <span className="app-muted text-xs">None assigned</span>
                        )}
                      </td>
                      <td className="text-right">
                        <Cell to={`${SAFETY_PAGES.toolbox.path}${q(row.branch)}`} value={row.latestTalkDone} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ListCard>
        </>
      )}
    </PageShell>
  );
};

export default SafetyOverview;
